import { useState, useEffect } from 'react';
import { PageHeader } from '@/components/mypage/PageHeader';
import { EventCard } from '@/components/mypage/EventCard';
import { useToast } from '@/components/common/Toast';
import { useLoadingCoordinator, SkeletonComponents } from '@/hooks/useLoadingCoordinator';
import { sellerEventsService } from '@/services/sellerEvents';
import type { Event } from '@/types';

export function SellerEventsPage() {
  const { showToast, ToastContainer } = useToast();
  const [events, setEvents] = useState<Event[]>([]);
  const [isDataLoading, setIsDataLoading] = useState(true);
  const [filter, setFilter] = useState<'all' | 'active' | 'upcoming'>('all');

  const { 
    shouldShowLoading, 
    shouldShowSkeleton, 
    shouldShowContent 
  } = useLoadingCoordinator(isDataLoading, {
    useSkeletonMode: true,
    minLoadingTime: 200
  });

  useEffect(() => {
    const fetchEvents = async () => {
      try {
        setIsDataLoading(true);
        const result = filter === 'active' 
          ? await sellerEventsService.getActiveEvents()
          : await sellerEventsService.getAllEvents();
        
        if (result.success && result.data) {
          let filteredEvents = result.data;
          
          if (filter === 'upcoming') {
            const now = new Date();
            filteredEvents = result.data.filter(event => new Date(event.start_date) > now);
          }
          
          // Transform seller events to match Event type
          const transformedEvents: Event[] = filteredEvents.map(event => ({
            id: event.id,
            name: event.name,
            description: event.description || '',
            event_type: 'seller_event',
            start_date: event.start_date,
            end_date: event.end_date,
            max_participants: undefined,
            current_participants: 0,
            reward_points: 0,
            reward_coupon_id: undefined,
            season: undefined,
            is_active: event.is_active,
            terms_and_conditions: undefined,
            created_at: event.created_at,
            updated_at: event.updated_at,
            image_url: undefined,
            summary: event.description || ''
          }));
          
          setEvents(transformedEvents);
        } else {
          showToast(result.error || '이벤트를 불러오는데 실패했습니다');
        }
      } catch (error) {
        console.error('Error fetching events:', error);
        showToast('이벤트를 불러오는데 실패했습니다');
      } finally {
        setIsDataLoading(false);
      }
    };

    fetchEvents();
  }, [filter, showToast]);

  const getFilterText = (filterType: typeof filter) => {
    switch (filterType) {
      case 'all': return '전체';
      case 'active': return '진행중';
      case 'upcoming': return '예정';
      default: return '전체';
    }
  };

  // 전체 화면 로딩 (인증 로딩)
  if (shouldShowLoading) {
    return (
      <div className="min-h-screen bg-white">
        <PageHeader title="이벤트" />
        <div className="flex items-center justify-center py-20">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-500 mx-auto mb-4"></div>
            <p className="text-gray-600">이벤트를 불러오는 중...</p>
          </div>
        </div>
      </div>
    );
  }

  // 스켈레톤 로딩 (데이터 로딩)
  if (shouldShowSkeleton) {
    return (
      <div className="min-h-screen bg-white">
        <PageHeader title="이벤트" />
        
        {/* 필터 탭 스켈레톤 */}
        <div className="px-4 py-4 border-b border-gray-200">
          <div className="flex space-x-1 bg-gray-100 rounded-lg p-1">
            {[1, 2, 3].map((_, index) => (
              <div key={index} className="flex-1 py-2 px-4">
                <div className="h-5 bg-gray-200 rounded animate-pulse"></div>
              </div>
            ))}
          </div>
        </div>
        
        <SkeletonComponents.EventCard count={3} />
      </div>
    );
  }

  // 콘텐츠 표시
  if (shouldShowContent) {
    return (
      <div className="min-h-screen bg-white">
        <PageHeader title="이벤트" />
        
        <div className="pb-6">
          {/* 필터 탭 */}
          <div className="px-4 py-4 border-b border-gray-200">
            <div className="flex space-x-1 bg-gray-100 rounded-lg p-1">
              {(['all', 'active', 'upcoming'] as const).map((filterType) => (
                <button
                  key={filterType}
                  onClick={() => setFilter(filterType)}
                  className={`flex-1 py-2 px-4 text-sm font-medium rounded-lg transition-colors ${
                    filter === filterType
                      ? 'bg-white text-[#222222] shadow-sm'
                      : 'text-gray-600 hover:text-[#222222]'
                  }`}
                >
                  {getFilterText(filterType)}
                </button>
              ))}
            </div>
          </div>

          {/* 이벤트 목록 */}
          <div className="px-4 py-4">
            {events.length === 0 ? (
              <div className="flex items-center justify-center py-20">
                <div className="text-center">
                  <div className="w-16 h-16 mx-auto mb-4 text-gray-300">
                    <svg width="64" height="64" viewBox="0 0 24 24" fill="none">
                      <rect x="3" y="4" width="18" height="18" rx="2" ry="2" stroke="currentColor" strokeWidth="2"/>
                      <line x1="16" y1="2" x2="16" y2="6" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                      <line x1="8" y1="2" x2="8" y2="6" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                      <line x1="3" y1="10" x2="21" y2="10" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                      <path d="M8 14H12" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                      <path d="M8 18H16" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                    </svg>
                  </div>
                  <p className="text-gray-600 mb-2">
                    {filter === 'active' && '진행 중인 이벤트가 없습니다'}
                    {filter === 'upcoming' && '예정된 이벤트가 없습니다'}
                    {filter === 'all' && '등록된 이벤트가 없습니다'}
                  </p>
                  <p className="text-gray-500 text-sm">새로운 이벤트가 등록되면 알려드릴게요</p>
                </div>
              </div>
            ) : (
              <div className="space-y-4">
                {events.map((event) => (
                  <EventCard key={event.id} event={event} />
                ))}
              </div>
            )}
          </div>
        </div>
        
        <ToastContainer />
      </div>
    );
  }

  // 폴백 (예상되지 않는 상태)
  return null;
}